import { useEffect } from 'react';
import { householdsService } from '../services/households-service';
import { useAuthStore } from '../stores/auth-store';
import { useCurrencyStore } from '../stores/currency-store';

/**
 * Keep the cached base currency in step with the household on the server.
 *
 * Mounted once in MainLayout. The store already hydrates from localStorage, so
 * this is a correction rather than a load -- the UI never waits on it.
 *
 * Reason: the cached value can go stale in a way the user cannot see, when
 * another household member changes the base currency. Every cross-account total
 * would then be labelled with a currency it is no longer converted into. That is
 * a wrong number wearing a plausible label, so it is worth one request per
 * session to correct.
 */
export function useBaseCurrencySync(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setBaseCurrency = useCurrencyStore((s) => s.setBaseCurrency);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    householdsService
      .fetchCurrentHousehold()
      .then((res) => {
        const household = res.data;
        if (cancelled || !household?.base_currency) return;
        setBaseCurrency(household.base_currency, household.id);
      })
      // A failure here leaves the cached value in place, which is the same
      // value the previous session used. Nothing to surface to the user.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, setBaseCurrency]);
}
